/**
 * src/main.tsx — Vite + React 18 entry point.
 *
 * Loading strategy during Phase 1A:
 *   1. Expose React + ReactDOM on window for legacy IIFE compat. The legacy
 *      files (src/legacy/*) were written when React was a UMD global; the
 *      cleanest way to support them under ESM without rewriting 7,000 LOC is
 *      to put React back on window before any legacy code runs.
 *   2. Import the legacy data layer (model, hooks, state-storage) as side
 *      effects — each IIFE publishes its API to window.{MONTH_KEYS,HOOKS,...}.
 *   3. Import the legacy view chunks (primitives, ae-chart, kpi-strip,
 *      forecast-table, inputs-panel) — also side-effect imports.
 *   4. Finally import the legacy `Dashboard` + `ErrorBoundary` from
 *      src/legacy/app and mount them via React Router.
 *
 * Phase 4 will replace this orchestration with proper ESM imports and the
 * v2 primitives. For Phase 1A the contract is "no behavior change".
 */
// ─── MUST be first: set up window globals BEFORE any legacy module imports.
// Imports are hoisted + executed in source order; setup-globals.ts's body runs
// fully (setting window.Zod) before ./lib/model is imported below.
import "./setup-globals";

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ViewerProvider } from "./lib/data/viewer";
// PR 1 (design system) — apply the persisted design preset BEFORE React mounts
// so the first paint matches the user's stored choice (no FOUC). Mirrors the
// pattern lib/theme.ts uses for data-theme.
import { getDesign, applyDesign } from "./lib/design";
applyDesign(getDesign());

// Load CSS — order matters:
//   tokens (legacy raw palette) → tokens (v2 overlay) → theme (dark mode) →
//   fonts → v2 primitive styles → legacy app styles.
//
// The legacy.css block (~2540 lines) was extracted from Forecast.html during
// Phase 1A. Phase 3 added components.css (v2 primitive class definitions —
// .v2-btn, .v2-card, .v2-kpi-strip, etc.) so the primitives have actual
// styling once Lab + Phase 4 import them.
import "/tokens.css";
import "./styles/tokens.css";
import "./styles/theme.css";
import "./styles/fonts.css";
import "./styles/components.css";
import "./styles/legacy.css";

// Legacy data layer (publishes window.MONTH_KEYS, window.computeForecast, window.HOOKS, etc.)
import "./lib/model";
import "./lib/hooks";
import "./lib/state-storage";

// Legacy view chunks (publish window.TOKENS, window.Button shells, etc.).
// Composed migrations (Phase 4):
//   - kpi-strip      sub-step 2  → composed/KpiStrip.tsx
//   - GrowthChart    sub-step 3  → composed/GrowthChart.tsx
//   - forecast-table sub-step 4  → composed/ForecastTable.tsx
//   - DealsView      sub-step 5  → composed/DealsView.tsx
//   - inputs-panel   sub-step 6  → composed/InputsPanel.tsx
//   - ae-chart       sub-step 10a → composed/AEChart.tsx
//   - Dashboard      sub-step 9  → views/ForecastItaly.tsx
// Only `./legacy/primitives` remains — the window-globaled compat
// shell (window.Button, window.Card, window.useEscapeClose, etc.)
// that every composed/* still reads at render time. Final removal
// of this shell is a Phase 5+ task (graduates composed/* to import
// v2 primitives from src/components/ui/ directly).
import "./legacy/primitives";

// The legacy Dashboard + ErrorBoundary — must be the LAST legacy import because
// it reads every window-global the above publish.
import App from "./App";

// React Router v6 future flags — opt-in early to v7 behavior. Suppresses the
// "future flag warning" noise in the console; the behavior changes are
// backward-compatible for our route table.
const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter future={routerFuture}>
      <ViewerProvider>
        <App />
      </ViewerProvider>
    </BrowserRouter>
  </React.StrictMode>
);
